import {
  CiOutlined,
  EnvironmentOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  RiseOutlined,
  SettingOutlined,
  TrophyOutlined
} from '@ant-design/icons';
import { Button, Card, Col, Form, Input, message, Row, Typography } from 'antd';
import React from 'react';
import { useAuth } from '../hooks/useAuth';
import { useChat } from '../hooks/useChat';
import { useIsMobile } from '../hooks/useIsMobile';
import { useI18n } from '../i18n';

const { Title, Paragraph, Text } = Typography;

const AboutPage: React.FC = () => {
  const { t } = useI18n();
  const { user, isAuthenticated, requireAuth } = useAuth();
  const { sendMessage: sendChatMessage, error: chatError } = useChat();
  const isMobile = useIsMobile();
  // 公司特色数据
  const companyFeatures = [
    {
      icon: <RiseOutlined style={{ fontSize: '32px', color: '#fa8c16' }} />,
      title: t('about.experience.title'),
      description: t('about.experience.description')
    },
    {
      icon: <SettingOutlined style={{ fontSize: '32px', color: '#722ed1' }} />,
      title: t('about.manufacturing.title'),
      description: t('about.manufacturing.description')
    },
    {
      icon: <TrophyOutlined style={{ fontSize: '32px', color: '#52c41a' }} />,
      title: t('about.innovation.title'),
      description: t('about.innovation.description')
    }
  ];

  // 技术团队数据
  const techTeam = [
    {
      icon: <GlobalOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      title: t('about.team.singapore.title'),
      description: t('about.team.singapore.description')
    },
    {
      icon: <CiOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
      title: t('about.team.taiwan.title'),
      description: t('about.team.taiwan.description')
    },
    {
      icon: <SettingOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      title: t('about.team.india.title'),
      description: t('about.team.india.description')
    }
  ];

  // 商业合作数据
  const businessCooperation = [
    {
      title: t('about.businessCooperation.retail.title'),
      subtitle: t('about.businessCooperation.retail.subtitle'),
      features: [
        t('about.businessCooperation.retail.features.0'),
        t('about.businessCooperation.retail.features.1'),
        t('about.businessCooperation.retail.features.2'),
        t('about.businessCooperation.retail.features.3')
      ],
      isHighlighted: false
    },
    {
      title: t('about.businessCooperation.wholesale.title'),
      subtitle: t('about.businessCooperation.wholesale.subtitle'),
      features: [
        t('about.businessCooperation.wholesale.features.0'),
        t('about.businessCooperation.wholesale.features.1'),
        t('about.businessCooperation.wholesale.features.2'),
        t('about.businessCooperation.wholesale.features.3')
      ],
      isHighlighted: false
    },
    {
      title: t('about.businessCooperation.designManufacturing.title'),
      subtitle: t('about.businessCooperation.designManufacturing.subtitle'),
      features: [
        t('about.businessCooperation.designManufacturing.features.0'),
        t('about.businessCooperation.designManufacturing.features.1'),
        t('about.businessCooperation.designManufacturing.features.2'),
        t('about.businessCooperation.designManufacturing.features.3')
      ],
      isHighlighted: true
    },
    {
      title: t('about.businessCooperation.oem.title'),
      subtitle: t('about.businessCooperation.oem.subtitle'),
      features: [
        t('about.businessCooperation.oem.features.0'),
        t('about.businessCooperation.oem.features.1'),
        t('about.businessCooperation.oem.features.2'),
        t('about.businessCooperation.oem.features.3')
      ],
      isHighlighted: false
    }
  ];

  // 联系信息数据
  const contactInfo = [
    {
      icon: <MailOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      title: t('about.contact.email.title'),
      content: t('about.contact.email.address'),
      note: t('about.contact.email.response')
    },
    {
      icon: <PhoneOutlined style={{ fontSize: '48px', color: '#52c41a' }} />,
      title: t('about.contact.phone.title'),
      content: t('about.contact.phone.number'),
      note: t('about.contact.phone.hours')
    },
    {
      icon: <EnvironmentOutlined style={{ fontSize: '48px', color: '#1890ff' }} />,
      title: t('about.contact.address.title'),
      content: t('about.contact.address.location'),
      note: t('about.contact.address.building')
    }
  ];

  const handleSubmitForm = async (values: any) => {
    if (!user) {
      message.error(t('contact.form.loginRequired'));
      return;
    }

    try {
      // 构建消息内容，包含表单所有信息
      const messageContent = `${t('contact.name')}: ${values.name}
${t('contact.email')}: ${values.email}
${t('contact.subject')}: ${values.subject}
${t('contact.message')}: ${values.message}`;

      // 直接发送消息到聊天接口
      await sendChatMessage(messageContent);

      message.success(t('contact.form.submitSuccess'));
    } catch (error) {
      console.error('发送表单内容失败:', error);
      message.error(t('contact.form.submitFailed'));
    }
  };

  const onFinish = (values: any) => {
    if (isAuthenticated) {
      handleSubmitForm(values);
    } else {
      // 如果用户未登录，先提示登录，登录成功后再提交表单
      requireAuth(() => handleSubmitForm(values));
    }
  };

  return (
    <div>

      <section className="py-5" style={{ width: '100%' }}>
        <img src={'/img/phone/27.jpg'} style={{ width: '100%' }} alt="spacing" className="img-fluid" />
      </section>

      {/* 聯絡我們 */}
      <section style={{ padding: '40px 0' }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px' }}>


          <Card
            style={{
              background: 'white',
              borderRadius: '20px',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)',
              border: '1px solid #e0e0e0'
            }}
            bodyStyle={{ padding: '48px' }}
          >
            <Title level={4} style={{
              textAlign: 'center',
              marginBottom: '32px',
              color: '#333'
            }}>
              {t('about.contact.form.title')}
            </Title>
            <Form onFinish={onFinish} layout="vertical">
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="name" label={t('about.contact.form.name')}>
                    <Input
                      placeholder={t('about.contact.form.namePlaceholder')}
                      size="large"
                      style={{ borderRadius: '10px' }}
                    />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item name="email" label={t('about.contact.form.email')}>
                    <Input
                      placeholder={t('about.contact.form.emailPlaceholder')}
                      size="large"
                      style={{ borderRadius: '10px' }}
                    />
                  </Form.Item>
                </Col>
              </Row>
              <Form.Item name="subject" label={t('about.contact.form.subject')}>
                <Input
                  placeholder={t('about.contact.form.subjectPlaceholder')}
                  size="large"
                  style={{ borderRadius: '10px' }}
                />
              </Form.Item>
              <Form.Item name="message" label={t('about.contact.form.message')}>
                <Input.TextArea
                  rows={4}
                  placeholder={t('about.contact.form.messagePlaceholder')}
                  style={{ borderRadius: '10px' }}
                />
              </Form.Item>
              <div style={{ textAlign: 'center' }}>
                <Button
                  type="primary"
                  size="large"
                  htmlType="submit"
                  style={{
                    borderRadius: '25px',
                    padding: '12px 30px',
                    fontWeight: 600,
                    height: 'auto'
                  }}
                >
                  {t('about.contact.form.submit')}
                </Button>
              </div>
            </Form>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
